import { useEffect, useRef } from 'react';
import { useAuth } from '@/auth';
import { fetchWithCsrf } from '@/common/ApiService';
import { clearPendingTermsConsent, getPendingTermsConsent } from './pendingConsent';

const ACCEPT_TERMS_URL = '/asyncapi/v2/users/accept-terms';

/**
 * Writes the server-side record of login consent once the user is authenticated,
 * consuming the marker the login modal set before sign-in. Idempotent on the server,
 * so a missed clear or a retry is harmless. Leaves the marker on failure so the next
 * authenticated load retries. Must be rendered inside AuthProvider.
 */
export function TermsConsentRecorder(): null {
  const { isAuthenticated } = useAuth();
  const inFlight = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || inFlight.current) return;
    const version = getPendingTermsConsent();
    if (!version) return;

    inFlight.current = true;
    void (async () => {
      try {
        await fetchWithCsrf(ACCEPT_TERMS_URL, {
          method: 'POST',
          data: { terms_version: version },
        });
        clearPendingTermsConsent();
      } catch {
        // Keep the marker; a later authenticated load will try again.
      } finally {
        inFlight.current = false;
      }
    })();
  }, [isAuthenticated]);

  return null;
}
